// services/api/devolucionCompraApi.ts
import api from '../../utils/axios';
import { AxiosError } from 'axios';

export interface IDevolucionCompra {
  Id: number;
  OrdenCompra: string;
  Estado: boolean; // bit(1) in DB: false = Pendiente, true = Procesada
  detalles: IDetalleDevolucionCompra[];
}

export interface IDetalleDevolucionCompra {
  Id: number;
  IdArticulo: number;
  Cantidad: number;
  IdMotivoDevolucionCompra: number;
  FechaDevolucion: Date;
  Observacion?: string;
}

// Interfaces actualizadas en devolucionCompraApi.ts
export interface ICompraFinalizadaResponse {
  message: string;
  data: ICompraFinalizadaData[];
}

export interface ICompraFinalizadaData {
  compra: {
    Id: number;
    OrdenCompra: string;
    FechaSolicitud: string;
    FechaEntrega: string;
    proveedor: {
      Id: number;
      NumeroIdentificacion: string;
      Nombre: string;
      RepresentanteLegal: string;
      Direccion: string;
      Telefono: string;
      Correo: string;
      Web: string;
    };
    estadoCompra: {
      Nombre: string;
    };
    usuario: {
      Usuario: string;
    };
  };
  articulosCompra: Array<{
    id: number;
    IdCompra: number;
    IdSucursal: number;
    IdArticulo: number;
    CantidadComprada: number;
    StockActual: number;
    Contador: number;
    PrecioCompra: string;
    FechaCompra: string;
    articulo: {
      Id: number;
      Nombre: string;
      // ... otros campos del artículo si son necesarios
    };
    sucursal: {
      Id: number;
      Nombre: string;
      // ... otros campos de la sucursal si son necesarios
    };
  }>;
  detalleCompra: {
    Id: number;
    IdCompra: number;
    IdTipoPago: number;
    FechaRecibo: string;
    NumeroFactura: string;
    ImgFactura: string | null;
    Saldo: number;
    Abono: number;
    Total: number;
    Observacion: string;
    tipoPago: {
      Id: number;
      Nombre: string;
      Activo: boolean;
      Eliminado: boolean;
    };
  };
}

class DevolucionCompraApi {
  private baseUrl = `/devoluciones-compra`;

  private handleError(error: AxiosError) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Error en el servidor');
    } else if (error.request) {
      throw new Error('No se pudo conectar con el servidor');
    } else {
      throw new Error('Error al procesar la solicitud');
    }
  }

  async create(devolucion: Partial<IDevolucionCompra>) {
    try {
      const response = await api.post(this.baseUrl, devolucion);
      return response.data;
    } catch (error) {
      this.handleError(error as AxiosError);
    }
  }

  async findAll() {
    try {
      const response = await api.post(`${this.baseUrl}/listar`);
      return response.data;
    } catch (error) {
      this.handleError(error as AxiosError);
    }
  }

  async findOne(id: number) {
    try {
      const response = await api.post(`${this.baseUrl}/buscar/${id}`);
      return response.data;
    } catch (error) {
      this.handleError(error as AxiosError);
    }
  }

  async procesarDevolucion(id: number) {
    try {
      const response = await api.post(`${this.baseUrl}/procesar/${id}`);
      return response.data;
    } catch (error) {
      this.handleError(error as AxiosError);
    }
  }

  // Nuevo método para obtener compras finalizadas por número de orden
  async getCompraFinalizada(ordenCompra: string): Promise<ICompraFinalizadaResponse> {
    const response = await api.post(`${this.baseUrl}/compras-finalizadas`, {
      ordenCompra
    });
    return response.data;
  }

}

export const devolucionCompraApi = new DevolucionCompraApi();
export default devolucionCompraApi;